import type { Unit } from './primitives.js'

/**
 * Formateo es-AR. Vive en `contracts` y no en el front porque `narrativa.ts`
 * arma prosa con los mismos números que muestran las tarjetas: si hubiera dos
 * implementaciones, el titular y la tarjeta podrían decir cifras distintas.
 * `apps/web` debe re-exportar de acá, no reimplementar.
 */

/** Signo menos tipográfico U+2212. Alinea mejor que el guion y no se confunde
 *  con un separador cuando está pegado a un número. */
export const MENOS = '−'

/** Guion largo para celdas de tabla sin dato. NO se usa en tarjetas de KPI:
 *  ahí la spec exige EmptyMetricState con invitación a cargar. */
export const SIN_DATO = '—'

const MS_POR_DIA = 86_400_000

/** Días que consideramos "un mes" para el relativo. Aproximación deliberada:
 *  el badge de frescura no necesita precisión de calendario. */
const DIAS_POR_MES = 30

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const

/**
 * Formatea el módulo del número con `digits` decimales en es-AR (coma decimal,
 * punto de miles) y le pega el signo menos tipográfico si hace falta.
 *
 * `useGrouping: true` es explícito a propósito: el CLDR del español tiene
 * minimumGroupingDigits = 2, así que con el default ("auto") un 1480 saldría
 * "1480" sin punto. En una columna de cifras eso se lee mal.
 */
function formatNumero(valor: number, digits: number): string {
  const cuerpo = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  }).format(Math.abs(valor))

  return valor < 0 ? `${MENOS}${cuerpo}` : cuerpo
}

/**
 * Dólares. Arriba de mil los centavos son ruido; abajo de mil importan
 * (ticket promedio, costo unitario).
 */
function formatUsd(valor: number): string {
  const digits = Math.abs(valor) >= 1000 ? 0 : 2
  const cuerpo = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  }).format(Math.abs(valor))

  return `${valor < 0 ? MENOS : ''}$${cuerpo}`
}

/** Valor de una métrica listo para mostrar, según su unidad. */
export function formatMetricValue(valor: number | null, unidad: Unit): string {
  if (valor === null) return SIN_DATO

  switch (unidad) {
    case 'usd':
      return formatUsd(valor)
    case 'percent':
      return `${formatNumero(valor, 1)}%`
    case 'count':
      return formatNumero(valor, 0)
    case 'months':
      // Siempre plural: al llevar decimal, "1,0 mes" suena peor que "1,0 meses".
      return `${formatNumero(valor, 1)} meses`
    case 'ratio':
      return `${formatNumero(valor, 1)}x`
    case 'position':
      return formatNumero(valor, 1)
    default: {
      const exhaustivo: never = unidad
      return String(exhaustivo)
    }
  }
}

/**
 * Variación porcentual contra el período anterior. Devuelve null cuando no hay
 * comparable, para que quien lo llame no renderice el elemento.
 */
export function formatDelta(deltaPct: number | null): string | null {
  if (deltaPct === null) return null

  const cuerpo = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: true,
  }).format(Math.abs(deltaPct))

  if (deltaPct === 0) return `${cuerpo}%`

  return `${deltaPct < 0 ? MENOS : '+'}${cuerpo}%`
}

/**
 * Antigüedad en lenguaje natural. Recibe `now` por parámetro en vez de leer el
 * reloj: así los tests son deterministas y el badge de frescura puede compartir
 * un mismo "ahora" con el resto de la pantalla.
 */
export function formatRelativeDays(iso: string | null, now: Date): string {
  if (iso === null) return 'nunca'

  const entonces = new Date(iso)
  if (Number.isNaN(entonces.getTime())) return 'nunca'

  const dias = Math.floor((now.getTime() - entonces.getTime()) / MS_POR_DIA)

  if (dias <= 0) return 'hoy'
  if (dias === 1) return 'ayer'
  if (dias < DIAS_POR_MES) return `hace ${dias} días`

  const meses = Math.floor(dias / DIAS_POR_MES)
  if (meses === 1) return 'hace 1 mes'

  return `hace ${meses} meses`
}

/**
 * 'YYYY-MM' → 'julio 2026'. Es la base que la regla de números derivables
 * exige publicar al lado de cada delta.
 */
export function formatPeriodo(periodo: string): string {
  const mes = mesDe(periodo)
  if (mes === null) return periodo
  return `${MESES[mes.indice]} ${mes.anio}`
}

/** 'YYYY-MM' → 'julio'. Para cuando el año ya está en el contexto. */
export function formatPeriodoCorto(periodo: string): string {
  const mes = mesDe(periodo)
  if (mes === null) return periodo
  return MESES[mes.indice] as string
}

function mesDe(periodo: string): { anio: number; indice: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(periodo)
  if (m === null) return null
  const anio = Number(m[1])
  const indice = Number(m[2]) - 1
  if (indice < 0 || indice > 11) return null
  return { anio, indice }
}

/** 'YYYY-MM' → el período anterior. Sirve para nombrar la base de un delta. */
export function periodoAnterior(periodo: string): string | null {
  const mes = mesDe(periodo)
  if (mes === null) return null
  const indice = mes.indice - 1
  const anio = indice < 0 ? mes.anio - 1 : mes.anio
  const normalizado = indice < 0 ? 11 : indice
  return `${anio}-${String(normalizado + 1).padStart(2, '0')}`
}

/** ['a','b','c'] → 'a, b y c'. Enumeración en prosa castellana. */
export function listaEs(items: readonly string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0] as string
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`
}
